import {
  ArrowRight,
  GraduationCap,
  HardDrive,
  QrCode,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useEffect, useState, type ComponentType } from "react";
import { useApp } from "../state/AppContext";
import type { Role } from "../types";
import { SchoolMark } from "./ui";

interface RoleChoice {
  role: Role;
  label: string;
  title: string;
  description: string;
  scope: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
}

const roleChoices: Record<Role, RoleChoice> = {
  admin: {
    role: "admin",
    label: "Admin Sekolah",
    title: "Urus operasi sekolah",
    description: "Pantau semua kelas, murid, laporan dan tetapan akses.",
    scope: "Akses demo seluruh sekolah",
    icon: ShieldCheck,
  },
  teacher: {
    role: "teacher",
    label: "Guru Kelas",
    title: "Rekod kehadiran kelas",
    description: "Imbas QR dan semak murid dalam kelas 3 Amanah.",
    scope: "Akses demo kelas sendiri",
    icon: GraduationCap,
  },
};

const mobileQuery = "(max-width: 767px)";

export function LoginScreen() {
  const { login } = useApp();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(mobileQuery).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(mobileQuery);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const orderedRoles: Role[] = isMobile ? ["teacher", "admin"] : ["admin", "teacher"];

  return (
    <main className="login-screen">
      <section className="login-brand" aria-labelledby="login-product-name">
        <div className="login-brand__content">
          <div className="login-brand__prototype">
            <Sparkles size={15} aria-hidden="true" />
            Prototaip interaktif
          </div>

          <SchoolMark inverse />

          <div className="login-brand__message">
            <span className="login-brand__eyebrow">Pengurusan murid &amp; kehadiran</span>
            <h1 id="login-product-name">Kehadiran jelas.<br />Setiap murid terjaga.</h1>
            <p>
              Satu ruang kerja untuk profil murid, rekod kehadiran dan imbasan QR
              harian di SK St Joseph (M) Papar.
            </p>
          </div>

          <div className="login-brand__principle">
            <QrCode size={20} aria-hidden="true" />
            <span>
              <strong>Satu QR untuk setiap murid</strong>
              Profil dan rekod kehadiran dapat dicapai dalam satu aliran.
            </span>
          </div>
        </div>

        <div className="login-brand__school-line" aria-hidden="true">
          <span className="login-brand__torch" />
          <span className="login-brand__book login-brand__book--left" />
          <span className="login-brand__book login-brand__book--right" />
        </div>

        <p className="login-brand__motto">Pendidikan Kunci Kejayaan</p>
      </section>

      <section className="login-access" aria-labelledby="login-heading">
        <div className="login-access__inner">
          <header className="login-access__header">
            <span className="login-access__step">Pilih ruang demo</span>
            <h2 id="login-heading">Selamat datang ke JosephCare</h2>
            <p>Pilih peranan untuk melihat pengalaman yang akan digunakan oleh pihak sekolah.</p>
          </header>

          <div className="login-role-list">
            {orderedRoles.map((role) => {
              const choice = roleChoices[role];
              const Icon = choice.icon;
              const descriptionId = `role-${role}-description`;
              return (
                <button
                  key={role}
                  type="button"
                  className={`login-role-card login-role-card--${role}`}
                  aria-describedby={descriptionId}
                  onClick={() => login(role)}
                >
                  <span className="login-role-card__topline">
                    <span className="login-role-card__icon" aria-hidden="true">
                      <Icon size={23} strokeWidth={2} />
                    </span>
                    <span className="login-role-card__role">{choice.label}</span>
                    {role === "teacher" && (
                      <span className="login-role-card__recommended">Sesuai untuk telefon</span>
                    )}
                  </span>

                  <span className="login-role-card__copy">
                    <strong>{choice.title}</strong>
                    <span id={descriptionId}>{choice.description}</span>
                  </span>

                  <span className="login-role-card__footer">
                    <span>{choice.scope}</span>
                    <span className="login-role-card__arrow" aria-hidden="true">
                      <ArrowRight size={18} />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <aside className="login-demo-note">
            <span className="login-demo-note__icon" aria-hidden="true">
              <HardDrive size={18} />
            </span>
            <div>
              <strong>Data demo, bukan rekod sebenar</strong>
              <p>
                Perubahan disimpan pada pelayar peranti ini. Tiada server sekolah,
                akaun sebenar atau notifikasi penjaga disambungkan.
              </p>
            </div>
          </aside>

          <p className="login-access__help">
            Tiada kata laluan diperlukan untuk prototaip ini.
          </p>
        </div>
      </section>
    </main>
  );
}
